/**
 * Shared tool types — single source of truth for the GET /api/v4/tools
 * response shape (server `sendJson` and the web `useTools` both import it).
 *
 * The wire payload is built server-side from the in-process tool registry
 * at `src/services/skills/tools/index.ts`. Each user-facing tool's default
 * export carries a `display: { label, icon, short, long }` block alongside
 * its `schema` and `execute` — display copy lives next to the
 * implementation. The code-side `slug` (== `schema.name`) IS the canonical
 * tool ID; the agent loop resolves by that name.
 *
 * Per project rule (Schemas Are the Source of Truth): the Zod schema is
 * canonical and the TS type is `z.infer<typeof ToolListItemSchema>`.
 * Wire data is observational-validated through `validateResponse` in
 * `useTools` so a server shape change surfaces as a logged warning.
 */

import { z } from 'zod';

export const ToolListItemSchema = z.object({
  /** Stable string ID. Must match a key in the code-side `toolMap`. */
  slug: z.string(),
  /** Title-cased label for the UI (e.g. "Account Intelligence"). */
  label: z.string(),
  /** Single emoji glyph rendered in the row + chip. */
  icon: z.string(),
  /** One-line summary used in the row + popover. */
  short: z.string(),
  /** Full description shown in the docked detail panel + chip expansion. */
  long: z.string(),
});

export const ToolListSchema = z.array(ToolListItemSchema);

/** A row of GET /api/v4/tools — display metadata only. */
export type ToolListItem = z.infer<typeof ToolListItemSchema>;
